// @flow
import React, { PureComponent } from "react";
import { compose } from "redux";
import { connect, Dispatch } from "react-redux";
import { type SearchResultDTO, type SearchResultRowDTO } from "data/types";
import Constants, { type ConstantsEnum } from "data/constants";
import Status from "components/Status";
import * as s from "data/reducers/selectors";
import NoData from "components/NoData";
import Button from "react-bootstrap/lib/Button";
import loc from "i18next";
import { userLanguage } from "data/storeHelper";
import Toolbar from "./Toolbar";
import SettingValue, { type SettingValueProps } from "containers/SettingValue";
import Measure from "react-measure";

import FullCalendar from "@fullcalendar/react";
import "./index.css";
import "@fullcalendar/core";
import dayGridPlugin from "@fullcalendar/daygrid";
import timeGridPlugin from "@fullcalendar/timegrid";
import listPlugin from "@fullcalendar/list";
import bootstrapPlugin from "@fullcalendar/bootstrap";

var colorArray = [
    "#FF6633",
    "#FFB399",
    "#FF33FF",
    "#FFFF99",
    "#00B3E6",
    "#E6B333",
    "#3366E6",
    "#999966",
    "#99FF99",
    "#B34D4D",
    "#80B300",
    "#809900",
    "#E6B3B3",
    "#6680B3",
    "#66991A",
    "#FF99E6",
    "#CCFF1A",
    "#FF1A66",
    "#E6331A",
    "#33FFCC",
    "#66994D",
    "#B366CC",
    "#4D8000",
    "#B33300",
    "#CC80CC",
    "#66664D",
    "#991AFF",
    "#E666FF",
    "#4DB3FF",
    "#1AB399",
    "#E666B3",
    "#33991A",
    "#CC9999",
    "#B3B31A",
    "#00E680",
    "#4D8066",
    "#809980",
    "#E6FF80",
    "#1AFF33",
    "#999933",
    "#FF3380",
    "#CCCC00",
    "#66E64D",
    "#4D80CC",
    "#9900B3",
    "#E64D66",
    "#4DB380",
    "#FF4D4D",
    "#99E6E6",
    "#6666FF",
];

// https://fullcalendar.io/docs/event-object
type EventInfo = {
    title: string,
    start: Date,
    allDay?: boolean,
    backgroundColor?: string,
    borderColor?: string,
    textColor?: string,
    extendedProps: { bc: string, row: Object, projection: string },
};

type Props = {
    /** Redux dispatch */
    dispatch: Dispatch,
    /** fetch status */
    status: ConstantsEnum,
    /** search results */
    result: ?SearchResultDTO,
    /** callback to load next page
     * @param {number} pageNum which page number to load
     */
    loadMoreRows: (pageNum: number) => Promise<SearchResultDTO>,
    /** callback when user clicks on a row
     * @param {number} index the row index clicked
     * @param {SearchResultRowDTO} row the row object itself
     */
    onMoreClick: (e, index: number, row: SearchResultRowDTO) => void,
    /** persisted calendar view */
    calendarView: SettingValueProps,
    /** Browser info */
    browser: Object,
};

type State = {
    events: ?Array<EventInfo>,
    projections: Object,
    pageNum: number,
    totalLoaded: number,
    isLoadingNextPage: boolean,
    hasMorePages: boolean,
    currentProjection: string,
    title: string,
};

const WrapperStyle = {
    flex: "1 1 100%",
    display: "flex",
    flexDirection: "column",
    minHeight: "0",
    minWidth: "0",
};

export class SearchResultCalendar extends PureComponent<Props, State> {
    _calendar: null | Calendar;

    constructor(props: Props) {
        super(props);
        this._calendar = React.createRef();

        this.state = {
            events: null,
            projections: {},
            pageNum: 0,
            totalLoaded: 0,
            isLoadingNextPage: false,
            hasMorePages: false,
            currentProjection: "",
            title: "loading",
        };
    }

    componentDidMount() {
        const { status, result } = this.props;
        if (status !== Constants.OK) return;
        else this._firstPage(result);
    }

    componentDidUpdate(prevProps: Props, prevState: State) {
        /* istanbul ignore else */
        if (
            this.props.status !== prevProps.status &&
            this.props.status === Constants.OK
        ) {
            this._firstPage(this.props.result);
        }
    }

    _firstPage = (result: SearchResultDTO) =>
        this.setState({
            events: this._getEventsFromResult(result),
            hasMorePages: result.totalCount > result.pageSize,
        });

    _getEventsFromResult = (result: SearchResultDTO) => {
        let events: Array<EventInfo> = [];
        let totalLoaded = this.state.totalLoaded;
        let projections = this.state.projections;
        Object.keys(result.rows).forEach((rowKey) => {
            totalLoaded++;
            const row = result.rows[rowKey];
            this._getEvents(row).forEach((attr) => {
                // collect legend and assign color
                if (projections[attr.name] == null) {
                    projections[attr.name] = {
                        color: colorArray[Object.keys(projections).length],
                        total: 1,
                    };
                } else {
                    projections[attr.name].total++;
                }
                // render event

                events.push({
                    id: rowKey,
                    title: row.title,
                    start: attr.date,
                    allDay: attr.allDay,
                    backgroundColor: projections[attr.name].color,
                    extendedProps: {
                        row,
                        projection: attr.name,
                        bc: row.breadcrumb.text,
                        // icon: (attr.value && attr.value.marker) || null
                    },
                });
            });
        });
        this.setState({ totalLoaded, projections });
        return events;
    };

    /** find all attributes with type Date_* */
    _getEvents = (row: Object) =>
        row.attributes
            .filter((a) => a.format && a.format.startsWith("Date_"))
            .map((a) => {
                const date = Date.parse(a.value.rawValue);

                if (isNaN(date)) {
                    console.warn("Could not parse Date", a.value.rawValue);
                    return null;
                }

                return {
                    date: date,
                    name: a.caption,
                    allDay: a.format === "Date_Date",
                };
            })
            .filter((a) => a);

    // TODO should really be loaded by Redux dispatch
    _loadNextPage = () => {
        this.setState({ isLoadingNextPage: true });
        this.props.loadMoreRows(this.state.pageNum + 1).then((result) => {
            const nextEvents = this._getEventsFromResult(result);
            this.setState({
                events: this.state.events.concat(nextEvents),
                pageNum: this.state.pageNum + 1,
                hasMorePages:
                    this.state.totalLoaded < this.props.result.totalCount,
                isLoadingNextPage: false,
            });
        });
    };

    _onEventDidMount = ({ event, el, view }) => {
        el.title = event.extendedProps.bc;

        // fullCalendar doesn't support the right click event out of the box so we have to add the event listener manually
        el.addEventListener(
            "contextmenu",
            (this.rightClickHandlers[event.id] =
                this._rightClickListener(event))
        );
    };

    rightClickHandlers = {};

    _rightClickListener(event) {
        const onMoreClick = this.props.onMoreClick;
        return function rightClickListener(jsEvent: Event) {
            jsEvent.preventDefault();
            onMoreClick(jsEvent, event.id, event.extendedProps.row);
        };
    }

    _onEventWillUnmount = ({ event, el, view }) => {
        el.removeEventListener(
            "contextmenu",
            this.rightClickHandlers[event.id]
        );
        delete this.rightClickHandlers[event.id];
    };

    _onEventClick = ({ event, el, jsEvent, view }) =>
        this.props.onClick(jsEvent, event.id, event.extendedProps.row);

    _onViewChange = ({ view, el }) =>
        this.props.calendarView.setValue(view.type);

    _onProjectionChange = (currentProjection) =>
        this.setState({ currentProjection });

    _getProjectedEvents = () =>
        this.state.currentProjection === ""
            ? this.state.events
            : this.state.events.filter(
                  (e) =>
                      e.extendedProps.projection ===
                      this.state.currentProjection
              );

    _onDatesRender = ({ view, el }) =>
        this.setState({
            title: view.title,
        });

    render() {
        const { status } = this.props;
        if (status !== Constants.OK) return <Status status={status} />;

        // TODO allow adding new items directly on calendar
        if (this.state.events == null || this.state.events.length === 0) {
            return <NoData locContext="calendar" />;
        }

        return (
            <>
                {this._calendar.current && (
                    <Toolbar
                        calendarRef={this._calendar.current}
                        browser={this.props.browser}
                        title={this.state.title}
                        currentView={this.props.calendarView.value}
                        currentProjection={this.state.currentProjection}
                        onProjectionChange={this._onProjectionChange}
                        projections={this.state.projections}
                        nextPageButton={
                            /* istanbul ignore next */
                            this.state.hasMorePages && (
                                <Button
                                    bsSize="small"
                                    disabled={this.state.isLoadingNextPage}
                                    onClick={this._loadNextPage}
                                    title={loc.t("location.loadNextPage")}
                                >
                                    {loc
                                        .t("location.loadNextPage")
                                        .substr(
                                            0,
                                            this.props.browser.lessThan.medium
                                                ? 1
                                                : undefined
                                        ) + " "}
                                    {this.state.isLoadingNextPage && (
                                        <Status
                                            inline
                                            status={Constants.LOADING}
                                        />
                                    )}
                                </Button>
                            )
                        }
                    />
                )}
                <Measure bounds>
                    {({
                        measureRef,
                        contentRect: /* istanbul ignore next */ { bounds } = {
                            bounds: { width: 0, height: 0 },
                        },
                    }) => (
                        <div style={WrapperStyle} ref={measureRef}>
                            <FullCalendar
                                ref={this._calendar}
                                height={
                                    /* istanbul ignore next */ bounds?.height ??
                                    0
                                }
                                locale={userLanguage().toLowerCase()}
                                initialView={this.state.currentView}
                                noEventsContent={loc.t("nodata")}
                                nowIndicator // https://fullcalendar.io/docs/nowIndicator
                                businessHours // https://fullcalendar.io/docs/businessHours
                                // eventLimit // https://fullcalendar.io/docs/eventLimit
                                headerToolbar={false}
                                plugins={[
                                    bootstrapPlugin,
                                    dayGridPlugin,
                                    timeGridPlugin,
                                    listPlugin,
                                ]}
                                themeSystem="bootstrap"
                                events={this._getProjectedEvents()}
                                eventDidMount={this._onEventDidMount}
                                eventWillUnmount={this._onEventWillUnmount}
                                eventClick={this._onEventClick}
                                viewDidMount={this._onViewChange}
                                datesSet={this._onDatesRender}
                            />
                        </div>
                    )}
                </Measure>
            </>
        );
    }
}

const mapStateToProps = /* istanbul ignore next */ (state, ownProps) => ({
    browser: state.browser,
    status: s.searchStatusSelector(state),
    result:
        s.searchStatusSelector(state) === Constants.OK
            ? s.searchResultSelector(state)
            : undefined,
    ...ownProps,
});

export default compose(
    connect(mapStateToProps),
    SettingValue({
        name: "searchResultCalendarView",
        namespace: "calendarView",
        defaultValue: "dayGridMonth",
    })
)(SearchResultCalendar);
